// User-facing display labels for the enum values CardConnect uses
// internally. We keep the raw codes in form state + on the wire (so
// nothing changes in the API), but the dropdowns + summaries render
// these friendlier strings.
//
// Single source of truth — both IntakeForm and NewApplication import
// from here so the UI stays consistent.

export interface LabeledOption<T extends string = string> {
  value: T;
  label: string;
}

// ----- Owner title ----------------------------------------------------------
//
// Valid title codes accepted by CardConnect for the principal owner on
// the application. Order matches the rough frequency we expect to see
// from PayPros (OWNER and PRESIDENT first, niche titles last).

export const OWNER_TITLE_OPTIONS: readonly LabeledOption[] = [
  { value: "OWNER", label: "Owner" },
  { value: "PRESIDENT", label: "President" },
  { value: "VICE_PRESIDENT", label: "Vice President" },
  { value: "CEO", label: "CEO" },
  { value: "CFO", label: "CFO" },
  { value: "COO", label: "COO" },
  { value: "MEMBER_LLC", label: "Member (LLC)" },
  { value: "PARTNER", label: "Partner" },
  { value: "SECRETARY", label: "Secretary" },
  { value: "TREASURER", label: "Treasurer" },
];

export const OWNER_TITLE_LABEL: Readonly<Record<string, string>> =
  Object.freeze(
    Object.fromEntries(OWNER_TITLE_OPTIONS.map((o) => [o.value, o.label])),
  );

export function ownerTitleLabel(code: string): string {
  return OWNER_TITLE_LABEL[code] ?? code;
}

// ----- Ownership type -------------------------------------------------------

export const OWNERSHIP_TYPE_OPTIONS: readonly LabeledOption[] = [
  { value: "PUBCORP", label: "Public Corporation" },
  { value: "PRIVCORP", label: "Private Corporation" },
  { value: "LLC", label: "LLC" },
  { value: "INDIVSOLE", label: "Individual / Sole Proprietor" },
  { value: "PRTNRSHP", label: "Partnership" },
  { value: "NONPRFT", label: "Nonprofit" },
  { value: "GOVT", label: "Government" },
  { value: "TAXEXMPT", label: "Tax Exempt" },
];

export const OWNERSHIP_TYPE_LABEL: Readonly<Record<string, string>> =
  Object.freeze(
    Object.fromEntries(OWNERSHIP_TYPE_OPTIONS.map((o) => [o.value, o.label])),
  );

export function ownershipTypeLabel(code: string): string {
  return OWNERSHIP_TYPE_LABEL[code] ?? code;
}

// ----- Account type ---------------------------------------------------------
//
// CardConnect previously accepted both PERS and PERSONAL for personal
// accounts. Standardize on BIZ / PERSONAL in the UI; both the public
// intake (which used PERSONAL) and the operator form (which used PERS)
// now agree. Anyone calling the backend with PERS continues to work
// server-side, but new submissions go out as PERSONAL.

export type AccountTypeCode = "BIZ" | "PERSONAL";

export const ACCOUNT_TYPE_OPTIONS: readonly LabeledOption<AccountTypeCode>[] = [
  { value: "BIZ", label: "Business" },
  { value: "PERSONAL", label: "Personal" },
];

export const ACCOUNT_TYPE_LABEL: Readonly<Record<string, string>> =
  Object.freeze({
    BIZ: "Business",
    PERSONAL: "Personal",
    PERS: "Personal",
  });

/** Normalize legacy PERS to PERSONAL — used when migrating old server
 *  values into the current UI state shape. */
export function normalizeAccountType(code: string): AccountTypeCode {
  if (code === "PERS" || code === "PERSONAL") return "PERSONAL";
  return "BIZ";
}

export function accountTypeLabel(code: string): string {
  return ACCOUNT_TYPE_LABEL[code] ?? code;
}

// ----- US states + territories ---------------------------------------------
//
// The full list of state codes we accept (matches VALID_STATES in
// formatters.ts plus Canadian provinces for cross-border merchants).
// Used by SelectField for the State input on addresses.

export const US_STATE_OPTIONS: readonly LabeledOption[] = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "DC", label: "District of Columbia" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
  // Territories / military
  { value: "AS", label: "American Samoa" },
  { value: "GU", label: "Guam" },
  { value: "MP", label: "Northern Mariana Islands" },
  { value: "PR", label: "Puerto Rico" },
  { value: "VI", label: "U.S. Virgin Islands" },
  { value: "AA", label: "Armed Forces Americas" },
  { value: "AE", label: "Armed Forces Europe" },
  { value: "AP", label: "Armed Forces Pacific" },
  { value: "FM", label: "Federated States of Micronesia" },
  { value: "MH", label: "Marshall Islands" },
  { value: "PW", label: "Palau" },
  // Canadian provinces (some PayPros merchants cross border)
  { value: "AB", label: "Alberta" },
  { value: "BC", label: "British Columbia" },
  { value: "MB", label: "Manitoba" },
  { value: "NB", label: "New Brunswick" },
  { value: "NL", label: "Newfoundland and Labrador" },
  { value: "NS", label: "Nova Scotia" },
  { value: "NT", label: "Northwest Territories" },
  { value: "NU", label: "Nunavut" },
  { value: "ON", label: "Ontario" },
  { value: "PE", label: "Prince Edward Island" },
  { value: "QC", label: "Quebec" },
  { value: "SK", label: "Saskatchewan" },
  { value: "YT", label: "Yukon" },
];

export const US_STATE_LABEL: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(US_STATE_OPTIONS.map((s) => [s.value, s.label])),
);

export function stateLabel(code: string): string {
  return US_STATE_LABEL[code] ?? code;
}
